/**
 * SetOpeningBalanceDialog - enter the opening balance for a bank account
 * whose statement couldn't yield one (iter 990). Posts the OB journal
 * (vs Opening Balance Equity, dated accounting start date − 1).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export const SetOpeningBalanceDialog = ({ account, open, onClose, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const value = parseFloat(amount);
    if (Number.isNaN(value)) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/bank-accounts/${account.id}/opening-balance`, { amount: value });
      toast.success('Opening balance saved and posted to your books');
      setAmount('');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save opening balance');
    } finally {
      setSaving(false);
    }
  };

  const name = account?.bank_name || account?.account_name || 'this account';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="set-opening-balance-dialog">
        <DialogHeader>
          <DialogTitle>Set opening balance</DialogTitle>
          <DialogDescription>
            What was the bank balance of <strong>{name}</strong> on your accounting
            start date? This posts an opening balance entry so your books match the bank.
            Enter a negative amount for an overdraft.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Input
            data-testid="opening-balance-amount-input"
            type="number"
            step="0.01"
            placeholder={`Balance in ${account?.currency || ''}`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="opening-balance-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || amount === ''} data-testid="opening-balance-save-btn">
            {saving ? 'Saving...' : 'Save & post'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SetOpeningBalanceDialog;
